import { useMemo } from 'react';
import { useAppSelector } from '@store/index';
import { selectUser } from '@features/auth/authSlice';

export interface TenantContext {
  tenantId: string | null;
  tenantCode: string | null;
  isMultiTenant: boolean;
  canAccessTenant: (tenantId: string) => boolean;
}

export const useTenantContext = (): TenantContext => {
  const user = useAppSelector(selectUser);

  const tenantContext = useMemo<TenantContext>(() => {
    if (!user) {
      return {
        tenantId: null,
        tenantCode: null,
        isMultiTenant: false,
        canAccessTenant: () => false,
      };
    }

    // Check if the system is multi-tenant
    const isMultiTenant = user.tenantSettings?.multiTenantEnabled || false;

    // Get allowed tenants for the user
    const allowedTenants = user.allowedTenants || [user.tenantId];

    return {
      tenantId: user.tenantId,
      tenantCode: user.tenantCode || null,
      isMultiTenant,
      canAccessTenant: (tenantId: string) => {
        // Admin users might have access to multiple tenants
        if (user.role === 'super_admin') {
          return true;
        }

        // Check if the tenant is in the allowed list
        return allowedTenants.includes(tenantId);
      },
    };
  }, [user]);

  return tenantContext;
};
